import React, { Component, Fragment } from 'react';
import { FACEBOOK_PIXEL_ID } from 'constants/vendor';

export const SCRIPT_URL = 'https://connect.facebook.net/en_US/fbevents.js';
const FACEBOOK_IMG = `https://www.facebook.com/tr?id=${FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1`;

/**
 * this script has a few modifications from the facebook-provided version:
 *
 * - the facebook id is externalized.
 *
 * - the script tag insertion is removed. since we're in react-land, the script
 *   tag is inserted via the facebook-events-lib script.
 *
 * - fbq is prefixed with 'window.' to be explicit.
 *
 * - the fbq('track', 'PageView') is moved to the FacebookPixel.pageView()
 *   method.
 *
 * - fbq has two options set: disablePushState and allowDuplicatePageViews.
 *   the combination of these values allow us to 1) directly control over
 *   exactly when the pageview is fired, and 2) call additional pageViews as we
 *   navigate to new pages via client-side routing. the former is important
 *   because, while fbevent.js automatically handles firing additional PageView
 *   events on pushState changes, our new page's data is not yet resolved when
 *   pushState occurs. it appears that allowDuplicatePageViews is automatically
 *   set on the pushState event listener.
 *
 * CLEANUP: re-evaluate the use of disablePushState and allowDuplicatePageViews
 * if we are ever able to ensure that pushState only gets called when our data
 * has resolved.
 *
 * NOTE: this needs to execute before the script loads because the script starts
 * by defining (more) properties on window.fbq. it only gets executed once: on
 * initial page render.
 *
 */

const FACEBOOK_QUEUE_JS = `
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];}(window,document);
  window.fbq.disablePushState = true;
  window.fbq.allowDuplicatePageViews = true;
  window.fbq('init', '${FACEBOOK_PIXEL_ID}');
`;

/**
 * this component and its integration approach is adapted from facebook-provided
 * JS pixel code so it works more reliably and optimized in our SPA.
 *
 * see https://developers.facebook.com/docs/facebook-pixel/implementation
 *
 * also see https://developers.facebook.com/ads/blog/post/2017/05/29/tagging-a-single-page-application-facebook-pixel/
 *
 * verify using Facebook Pixel Helper Chrome Extension
 * https://developers.facebook.com/docs/facebook-pixel/support/pixel-helper
 *
 * how to verify manually:
 *
 * - look for `facebook` in network requests
 * - one request for fevents.js on initial page render initiated by head-manager
 * - look for FACEBOOK_PIXEL_ID in network requests
 * - 307 -> 200 response .gif request for each PageView
 * - one Microdata request on initial pageview. unclear why this only happens for first PageView
 *
 */
class FacebookPixel extends Component {
  static Head() {
    if (!FACEBOOK_PIXEL_ID) {
      return null;
    }
    return (
      <Fragment>
        <script dangerouslySetInnerHTML={{ __html: FACEBOOK_QUEUE_JS }} />
        <script defer src={SCRIPT_URL} />
      </Fragment>
    );
  }
  render() {
    if (!FACEBOOK_PIXEL_ID) {
      return null;
    }
    return (
      <noscript
        dangerouslySetInnerHTML={{
          __html: `<img height="1" width="1" src="${FACEBOOK_IMG}"/>`
        }}
      />
    );
  }
}

FacebookPixel.pageView = () => {
  if (!window.fbq) {
    return;
  }
  window.fbq('track', 'PageView');
};

export default FacebookPixel;
